import React, {useCallback, useEffect, useRef, useMemo} from 'react';
import { calculateVisibleBeats, getGridLevel, calculateTotalWidth, calculatePosition as calculatePositionUtil } from '../utils/annotationUtils';
import { DEFAULT_PIXELS_PER_BEAT, getSubdivisionsForMeasure } from '../utils/constants';
import { GRID_LEVELS } from '../utils/constants';

/**
 * TimeGrid component - implementation with per-measure time signatures
 * Renders all grid elements without lazy loading
 */
const TimeGrid = ({
  timeSignatures = [],
  keyChanges = [],
  tempoChanges = [],
  beatsPerMeasureMap = [],
  measures = 16,
  zoomLevel = 1,
  pixelsPerBeat = 40,
  onTimeSignatureClick = () => {},
  setPlayheadX,
  setIsPlayheadDragging,
  scrollContainerRef,
  onGridLevelChange = () => {}
}) => {
  // Calculate actual pixels per beat based on zoom level
  const actualPixelsPerBeat = pixelsPerBeat * zoomLevel;
  
  // Calculate current grid level based on zoom and visible beats
  // Use a fixed viewport width for grid level calculation
  const viewportWidth = 1200;
  const visibleBeats = calculateVisibleBeats(viewportWidth, zoomLevel, DEFAULT_PIXELS_PER_BEAT);
  const currentGridLevel = getGridLevel(zoomLevel, visibleBeats);

  // Calculate bar number display interval based on zoom level
  const getBarNumberInterval = () => {
    const visibleMeasures = Math.ceil(visibleBeats / 4); // Approximate measures visible
    
    if (visibleMeasures <= 8) {
      return 1; // Show every bar number
    } else if (visibleMeasures <= 16) {
      return 2; // Show every 2nd bar number
    } else if (visibleMeasures <= 32) {
      return 4; // Show every 4th bar number
    } else {
      return 8; // Show every 8th bar number
    }
  };

  const barNumberInterval = getBarNumberInterval();

  // For playhead re-alignment on zoom level change
  const gridRef = useRef();

  const getNearestGridPosition = useCallback((clientX) => {
    const scrollContainer = scrollContainerRef.current;
    const bounds = scrollContainer.getBoundingClientRect();
    const relativeX = clientX - bounds.left + scrollContainer.scrollLeft;

    // Use the current grid level to determine snap size
    let snapSize;
    switch (currentGridLevel) {
      case GRID_LEVELS.SUBDIVISION:
        // For subdivision snapping, use a default subdivision count (4) for simplicity
        // In practice, this could be made more sophisticated to handle different time signatures
        snapSize = actualPixelsPerBeat / 4;
        break;
      case GRID_LEVELS.BEAT:
        snapSize = actualPixelsPerBeat;
        break;
      case GRID_LEVELS.BAR:
      default:
        // For bar level, snap to measure starts (assuming 4/4 time)
        snapSize = actualPixelsPerBeat * 4;
        break;
    }
    
    return Math.round(relativeX / snapSize) * snapSize;
  }, [actualPixelsPerBeat, currentGridLevel, scrollContainerRef]);

  const snapXToGrid = useCallback((x) => {
    // Use the current grid level to determine snap size
    let snapSize;
    switch (currentGridLevel) {
      case GRID_LEVELS.SUBDIVISION:
        // For subdivision snapping, use a default subdivision count (4) for simplicity
        // In practice, this could be made more sophisticated to handle different time signatures
        snapSize = actualPixelsPerBeat / 4;
        break;
      case GRID_LEVELS.BEAT:
        snapSize = actualPixelsPerBeat;
        break;
      case GRID_LEVELS.BAR:
      default:
        // For bar level, snap to measure starts (assuming 4/4 time)
        snapSize = actualPixelsPerBeat * 4;
        break;
    }
    
    return Math.round(x / snapSize) * snapSize;
  }, [actualPixelsPerBeat, currentGridLevel]);

  const handleMouseDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    const snappedX = getNearestGridPosition(e.clientX);
    setPlayheadX(snappedX);
    setIsPlayheadDragging(true);
  };

  useEffect(() => {
    setPlayheadX(prevX => snapXToGrid(prevX));
  }, [snapXToGrid]);
  
  // Notify parent component when grid level changes
  useEffect(() => {
    onGridLevelChange(currentGridLevel);
  }, [currentGridLevel, onGridLevelChange]);
  
  // Helper functions for subdivision conversion
  const toDisplaySubdivision = (subdivision) => (subdivision || 0) + 1; // 0-based to 1-based
  const toInternalSubdivision = (subdivision) => Math.max(0, (subdivision || 1) - 1); // 1-based to 0-based
  
  // Calculate position for a given measure, beat, and subdivision using centralized utility
  const calculatePosition = useCallback((measure, beat, subdivision = 1) => {
    return calculatePositionUtil(measure, beat, subdivision, beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures);
  }, [beatsPerMeasureMap, actualPixelsPerBeat, timeSignatures]);
  
  // Calculate total width using the centralized utility function
  const totalWidth = useMemo(() => 
    calculateTotalWidth(measures, beatsPerMeasureMap, actualPixelsPerBeat),
    [measures, beatsPerMeasureMap, actualPixelsPerBeat]
  );
  
  // Helper functions to get active key and tempo for a measure
  const getKeyForMeasure = (measure) => {
    // Find the most recent key change at or before this measure
    let activeKey = "C major"; // Default key
    
    for (const keyChange of keyChanges) {
      const keyMeasure = keyChange.position.bar;
      if (keyMeasure <= measure) {
        activeKey = `${keyChange.value.note} ${keyChange.value.mode}`;
      } else {
        break; // Key changes should be sorted by position
      }
    }
    
    return activeKey;
  };
  
  const getTempoForMeasure = (measure) => {
    // Find the most recent tempo change at or before this measure
    let activeTempo = "120"; // Default tempo
    
    for (const tempoChange of tempoChanges) {
      const tempoMeasure = tempoChange.position.bar;
      if (tempoMeasure <= measure) {
        activeTempo = tempoChange.value.toString();
      } else {
        break; // Tempo changes should be sorted by position
      }
    }
    
    return activeTempo;
  };

  // Change detection functions
  const hasTimeSignatureChangeAt = (measure, beat, subdivision) => {
    // For time signature, check if it's different from previous position
    if (measure === 1 && beat === 1 && subdivision === 1) {
      return false; // First position is not a "change"
    }
    
    // Get current time signature
    const currentTimeSig = timeSignatures[measure - 1];
    
    // Get previous time signature
    let prevTimeSig;
    if (beat === 1 && subdivision === 1) {
      // Beginning of measure, check previous measure
      prevTimeSig = measure > 1 ? timeSignatures[measure - 2] : timeSignatures[0];
    } else {
      // Same measure, so same time signature
      prevTimeSig = currentTimeSig;
    }
    
    return currentTimeSig !== prevTimeSig;
  };

  const hasKeyChangeAt = (measure, beat, subdivision) => {
    return keyChanges.some(change => 
      change.position.bar === measure &&
      change.position.beat === beat &&
      change.position.subdivision === subdivision
    );
  };

  const hasTempoChangeAt = (measure, beat, subdivision) => {
    return tempoChanges.some(change =>
      change.position.bar === measure &&
      change.position.beat === beat && 
      change.position.subdivision === subdivision
    );
  };

  const getChangesAtPosition = (measure, beat, subdivision) => {
    return {
      timeSignatureChanged: hasTimeSignatureChangeAt(measure, beat, subdivision),
      keyChanged: hasKeyChangeAt(measure, beat, subdivision),
      tempoChanged: hasTempoChangeAt(measure, beat, subdivision)
    };
  };

  const getBarHeight = () => {
    // Always use consistent height for main bars
    return '12px';
  };

  const getExtensionBars = (changes, changeType, position, color) => {
    const extensionBars = [];
    
    // Define the new positions with proper spacing to avoid overlap
    const positions = {
      tempo: -70,        
      key: -50,         
      timeSignature: -30 
    };
    
    // Main bar height
    const mainBarHeight = 12;
    
    // STEPPED EXTENSION LENGTHS: Create cascading visual effect
    const extensionLengths = {
      tempo: 55,         // Longest extension
      key: 35,           // Medium extension
      timeSignature: 10  // Shortest extension
    };
    
    // Each bar type gets a different extension length for cascading effect
    if (changeType === 'tempo') {
      extensionBars.push(
        <div 
          key={`${changeType}-ext-${position}`}
          style={{
            position: 'absolute',
            left: position,
            top: positions.tempo + mainBarHeight, // Start right after main tempo bar (-48px)
            width: '2px',
            height: `${extensionLengths.tempo}px`, // 42px extension
            backgroundColor: color,
            marginRight: '2px'
          }}
        />
      );
    } else if (changeType === 'key') {
      extensionBars.push(
        <div 
          key={`${changeType}-ext-${position}`}
          style={{
            position: 'absolute',
            left: position,
            top: positions.key + mainBarHeight, // Start right after main key bar (-28px)
            width: '2px',
            height: `${extensionLengths.key}px`, // 28px extension
            backgroundColor: color,
            marginRight: '2px'
          }}
        />
      );
    } else if (changeType === 'timeSignature') {
      extensionBars.push(
        <div 
          key={`${changeType}-ext-${position}`}
          style={{
            position: 'absolute',
            left: position,
            top: positions.timeSignature + mainBarHeight, // Start right after main time sig bar (-8px)
            width: '2px',
            height: `${extensionLengths.timeSignature}px`, // 14px extension
            backgroundColor: color,
            marginRight: '2px'
          }}
        />
      );
    }
    
    return extensionBars;
  };
  
  // Generate time signature markers - suppress regular display when time signature changes occur
  const renderTimeSignatures = useMemo(() => {
    const signatures = [];
    
    for (let i = 1; i <= measures; i++) {
      const position = calculatePosition(i, 1);
      
      // Check if there's a time signature change at this bar - if so, skip regular display
      const hasTimeSignatureChangeAtBar = hasTimeSignatureChangeAt(i, 1, 1);
      if (hasTimeSignatureChangeAtBar) {
        continue; // Skip showing regular time signature when there's a time signature change
      }
      
      // When bar interval is 8, only show time signatures where they change
      if (barNumberInterval === 8) {
        // Always show first measure, or show when time signature changes
        const shouldShowTimeSignature = i === 1 || 
          (i > 1 && timeSignatures[i-1] !== timeSignatures[i-2]);
        
        if (shouldShowTimeSignature) {
          signatures.push(
            <div 
              key={`timesig-${i}`}
              style={{
                position: 'absolute',
                left: position, // Position at the measure line
                top: -25, // NEW: Move to bottom position with time signature changes
                cursor: 'pointer',
                padding: '2px',
                border: '1px solid transparent',
                borderRadius: '3px',
                backgroundColor: '#fff',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
              onClick={(e) => onTimeSignatureClick(i, e)}
            >
              {timeSignatures[i-1]}
            </div>
          );
        }
      } else {
        // Normal behavior - show all time signatures
        signatures.push(
          <div 
            key={`timesig-${i}`}
            style={{
              position: 'absolute',
              left: position, // Position at the measure line
              top: -20, 
              cursor: 'pointer',
              padding: '2px',
              border: '1px solid transparent',
              borderRadius: '3px',
              backgroundColor: '#fff',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
            onClick={(e) => onTimeSignatureClick(i, e)}
          >
            {timeSignatures[i-1]}
          </div>
        );
      }
    }
    
    return signatures;
  }, [timeSignatures, measures, calculatePosition, onTimeSignatureClick, barNumberInterval, hasTimeSignatureChangeAt]);


  // Generate change indicators at bottom - always show all changes regardless of zoom level
  const renderChangeIndicators = useMemo(() => {
    const indicators = [];

    // Helper function to render a change indicator
    const renderChangeIndicator = (key, position, value, hasChanged, color, topOffset, changes, changeType) => {
      if (!hasChanged) return null;
      
      const barHeight = getBarHeight(changes, changeType);
      
      return (
        <div 
          key={key}
          style={{
            position: 'absolute',
            left: position,
            top: topOffset,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div 
            style={{
              width: '2px',
              height: barHeight,
              backgroundColor: color,
              marginRight: '2px'
            }}
          />
          <span style={{ 
            color: color,
            fontWeight: 'bold', // Match regular time signature font weight
            fontSize: '12px',   // Match regular time signature font size
            padding: '2px',     // Match regular time signature padding
            border: '1px solid transparent', // Match regular time signature border
            borderRadius: '3px', // Match regular time signature border radius
            backgroundColor: '#fff', // Match regular time signature background
          }}>
            {value}
          </span>
        </div>
      );
    };

    // Check all positions - ALWAYS show change indicators regardless of zoom level
    for (let measure = 1; measure <= measures; measure++) {
      const beatsInMeasure = beatsPerMeasureMap[measure-1];
      
      for (let beat = 1; beat <= beatsInMeasure; beat++) {
        // REMOVED: Skip non-first beats restriction - now always check all beats
        
        const position = calculatePosition(measure, beat);
        
        // Check for changes at beat level
        const changes = getChangesAtPosition(measure, beat, 1);
        
        // Tempo changes (blue) - NOW AT TOP
        if (changes.tempoChanged) {
          const currentTempo = getTempoForMeasure(measure);
          indicators.push(renderChangeIndicator(
            `tempo-change-${measure}-${beat}`,
            position,
            currentTempo,
            true,
            'blue',
            -60, // NEW: Tempo at top position (moved up 5px)
            changes,
            'tempo'
          ));
          // Add extension bars
          indicators.push(...getExtensionBars(changes, 'tempo', position, 'blue'));
        }
        
        // Key changes (green) - STAYS IN MIDDLE
        if (changes.keyChanged) {
          const currentKey = getKeyForMeasure(measure);
          indicators.push(renderChangeIndicator(
            `key-change-${measure}-${beat}`,
            position,
            currentKey,
            true,
            'green',
            -40, // SAME: Key in middle position
            changes,
            'key'
          ));
          // Add extension bars
          indicators.push(...getExtensionBars(changes, 'key', position, 'green'));
        }
        
        // Time signature changes (red) - NOW AT BOTTOM
        if (changes.timeSignatureChanged) {
          const currentTimeSig = timeSignatures[measure - 1];
          indicators.push(renderChangeIndicator(
            `timesig-change-${measure}-${beat}`,
            position,
            currentTimeSig,
            true,
            'red',
            -20, // NEW: Time signature at bottom position (moved down 5px)
            changes,
            'timeSignature'
          ));
          // Add extension bars
          indicators.push(...getExtensionBars(changes, 'timeSignature', position, 'red'));
        }

        // ALWAYS check subdivisions - removed zoom level restriction
        const subdivisionsPerBeat = getSubdivisionsForMeasure(timeSignatures, measure - 1);
        
        if (subdivisionsPerBeat > 0) {
          for (let sub = 2; sub <= subdivisionsPerBeat; sub++) {
            const subPosition = position + ((sub - 1) / subdivisionsPerBeat) * actualPixelsPerBeat;
            const subChanges = getChangesAtPosition(measure, beat, sub);
            
            // Tempo changes at subdivision level (blue) - NOW AT TOP
            if (subChanges.tempoChanged) {
              const currentTempo = getTempoForMeasure(measure);
              indicators.push(renderChangeIndicator(
                `tempo-change-${measure}-${beat}-${sub}`,
                subPosition,
                currentTempo,
                true,
                'blue',
                -60, // NEW: Tempo at top position (moved up 5px)
                subChanges,
                'tempo'
              ));
              // Add extension bars
              indicators.push(...getExtensionBars(subChanges, 'tempo', subPosition, 'blue'));
            }
            
            // Key changes at subdivision level (green) - STAYS IN MIDDLE
            if (subChanges.keyChanged) {
              const currentKey = getKeyForMeasure(measure);
              indicators.push(renderChangeIndicator(
                `key-change-${measure}-${beat}-${sub}`,
                subPosition,
                currentKey,
                true,
                'green',
                -40, // SAME: Key in middle position
                subChanges,
                'key'
              ));
              // Add extension bars
              indicators.push(...getExtensionBars(subChanges, 'key', subPosition, 'green'));
            }
            
            // Time signature changes at subdivision level (red) - NOW AT BOTTOM
            if (subChanges.timeSignatureChanged) {
              const currentTimeSig = timeSignatures[measure - 1];
              indicators.push(renderChangeIndicator(
                `timesig-change-${measure}-${beat}-${sub}`,
                subPosition,
                currentTimeSig,
                true,
                'red',
                -20, // NEW: Time signature at bottom position (moved down 5px)
                subChanges,
                'timeSignature'
              ));
              // Add extension bars
              indicators.push(...getExtensionBars(subChanges, 'timeSignature', subPosition, 'red'));
            }
          }
        }
      }
    }
    
    return indicators;
  }, [measures, beatsPerMeasureMap, calculatePosition, actualPixelsPerBeat, timeSignatures, keyChanges, tempoChanges, getChangesAtPosition, getKeyForMeasure, getTempoForMeasure, getBarHeight]);
  
  // Generate measure markers and numbers based on interval
  const renderMeasureMarkers = useMemo(() => {
    const markers = [];
    
    for (let i = 1; i <= measures; i++) {
      const position = calculatePosition(i, 1);
      
      // Measure line - always show all measure lines
      markers.push(
        <div 
          key={`measure-${i}`}
          style={{
            position: 'absolute',
            left: position,
            top: 0,
            width: 1,
            height: 20,
            backgroundColor: '#000',
          }}
        />
      );
      
      // Show measure label logic
      let shouldShowLabel;
      if (barNumberInterval === 8) {
        // When bar interval is 8, show both regular intervals AND time signature changes
        const isRegularInterval = (i - 1) % barNumberInterval === 0;
        const isTimeSignatureChange = i === 1 || (i > 1 && timeSignatures[i-1] !== timeSignatures[i-2]);
        shouldShowLabel = isRegularInterval || isTimeSignatureChange;
      } else {
        // Normal behavior - show measure label at calculated intervals starting from measure 1
        shouldShowLabel = (i - 1) % barNumberInterval === 0;
      }
      
      if (shouldShowLabel) {
        markers.push(
          <div 
            key={`measure-label-${i}`}
            style={{
              position: 'absolute',
              left: position + 4, // Move label a bit to the right to avoid overlap
              top: 15, // Normal position below the bars
              fontSize: 14, // Increase from 12 to 14
              fontWeight: 600, // Add bold
              zIndex: 2, // Ensure label is above grid lines
            }}
          >
            {i}
          </div>
        );
      }
    }
    
    // Add the N+1 measure marker at the end
    const nPlusOnePosition = calculatePosition(measures + 1, 1);
    markers.push(
      <div 
        key={`measure-end`}
        style={{
          position: 'absolute',
          left: nPlusOnePosition,
          top: 0,
          width: 2, // Make it slightly thicker to indicate end
          height: 20,
          backgroundColor: '#000',
        }}
      />
    );
    
    return markers;
  }, [measures, calculatePosition, barNumberInterval, timeSignatures]);
  
  // Generate beat markers based on current grid level - render all of them
  const renderBeatMarkers = useMemo(() => {
    const markers = [];
    const showBeats = currentGridLevel === GRID_LEVELS.BEAT || currentGridLevel === GRID_LEVELS.SUBDIVISION;
    const showSubdivisions = currentGridLevel === GRID_LEVELS.SUBDIVISION;

    for (let measure = 1; measure <= measures; measure++) {
      const beatsInMeasure = beatsPerMeasureMap[measure-1];
      
      for (let beat = 1; beat <= beatsInMeasure; beat++) {
        const position = calculatePosition(measure, beat);
        
        // Skip the first beat of each measure as it's already marked by the measure marker
        // But we still need to render its subdivisions
        if (beat !== 1 && showBeats) {
          markers.push(
            <div 
              key={`beat-${measure}-${beat}`}
              style={{
                position: 'absolute',
                left: position,
                top: 0,
                width: 1,
                height: 10,
                backgroundColor: '#000',
              }}
            />
          );
          
          // Position beat labels at the same level as measure labels
          markers.push(
            <div 
              key={`beat-label-${measure}-${beat}`}
              style={{
                position: 'absolute',
                left: position + 2,
                top: 10, // Position the label at 10
                fontSize: 10,
                zIndex: 2, // Ensure label is above grid lines
              }}
            >
              {beat}
            </div>
          );
        }

        // Subdivision markers - process for all beats, including the first one
        if (showSubdivisions) {
          // Get subdivisions per beat for this measure
          const subdivisionsPerBeat = getSubdivisionsForMeasure(timeSignatures, measure - 1);
          
          // Only render subdivisions if there are any for this measure
          if (subdivisionsPerBeat > 0) {
            // Create subdivision lines in each beat (excluding the beat position itself)
            for (let sub = 2; sub <= subdivisionsPerBeat; sub++) {
              // Calculate subdivision position: starting position is the beat itself, then calculate offset based on subdivision number
              const subPosition = position + ((sub - 1) / subdivisionsPerBeat) * actualPixelsPerBeat;
              
              markers.push(
                <div 
                  key={`beat-${measure}-${beat}-${sub}`}
                  style={{
                    position: 'absolute',
                    left: subPosition,
                    top: 0,
                    width: 1,
                    height: 5,
                    backgroundColor: '#666', // All subdivisions use the same color
                  }}
                />
              );
            }
          }
        }
      }
    }
    return markers;
  }, [measures, beatsPerMeasureMap, calculatePosition, currentGridLevel, actualPixelsPerBeat]);
  
  // Generate grid lines based on current grid level - render all of them
  const renderGridLines = useMemo(() => {
    const lines = [];
    const showBeats = currentGridLevel === GRID_LEVELS.BEAT || currentGridLevel === GRID_LEVELS.SUBDIVISION;
    const showSubdivisions = currentGridLevel === GRID_LEVELS.SUBDIVISION;

    for (let measure = 1; measure <= measures; measure++) {
      const beatsInMeasure = beatsPerMeasureMap[measure-1];
      
      for (let beat = 1; beat <= beatsInMeasure; beat++) {
        const position = calculatePosition(measure, beat);
        const isMeasureLine = beat === 1;
        
        // Always show measure lines (bar level)
        if (isMeasureLine) {
          lines.push(
            <div 
              key={`grid-${measure}-${beat}`}
              style={{
                position: 'absolute',
                left: position,
                top: 30,
                width: 2, // Make measure lines thicker
                height: 'calc(100% - 30px)',
                backgroundColor: '#000',
                zIndex: 1, // Higher z-index for measure lines
              }}
            />
          );
        }
        // Show beat lines only if current grid level allows it
        else if (showBeats) {
          lines.push(
            <div 
              key={`grid-${measure}-${beat}`}
              style={{
                position: 'absolute',
                left: position,
                top: 30,
                width: 1,
                height: 'calc(100% - 30px)',
                backgroundColor: '#666',
                zIndex: 0,
              }}
            />
          );
        }

        // Subdivision lines - process for all beats, including the first one
        if (showSubdivisions) {
          // Get subdivisions per beat for this measure
          const subdivisionsPerBeat = getSubdivisionsForMeasure(timeSignatures, measure - 1);
          
          // Only render subdivisions if there are any for this measure
          if (subdivisionsPerBeat > 0) {
            for (let sub = 2; sub <= subdivisionsPerBeat; sub++) {
              const subPosition = position + ((sub - 1) / subdivisionsPerBeat) * actualPixelsPerBeat;
              
              lines.push(
                <div 
                  key={`grid-${measure}-${beat}-${sub}`}
                  style={{
                    position: 'absolute',
                    left: subPosition,
                    top: 30,
                    width: 1,
                    height: 'calc(100% - 30px)',
                    backgroundColor: '#666', // All subdivisions use the same color
                    zIndex: 0,
                  }}
                />
              );
            }
          }
        }
      }
    }
    
    // Add the N+1 bar line at the end of all measures
    const nPlusOneBarPosition = calculatePosition(measures + 1, 1, 1);
    lines.push(
      <div 
        key={`grid-end-bar`}
        style={{
          position: 'absolute',
          left: nPlusOneBarPosition,
          top: 30,
          width: 2, // Make it thick like measure lines
          height: 'calc(100% - 30px)',
          backgroundColor: '#000',
          zIndex: 1, // Higher z-index for measure lines
        }}
      />
    );
    
    return lines;
  }, [measures, beatsPerMeasureMap, calculatePosition, currentGridLevel, actualPixelsPerBeat]);
  
  return (
    <div 
      data-testid="time-grid-surface"
      onMouseDown={handleMouseDown}
      style={{
        position: 'relative',
        width: totalWidth,
        height: '100%',
        minHeight: 120,
        overflowX: 'visible',
        backgroundColor: '#fff',
        borderBottom: '1px solid #000',
        paddingTop: '80px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <div style={{ position: 'relative', height: '100%' }}>
        <div
          data-testid="time-grid-ruler"
          style={{ height: 30, position: 'relative', cursor: 'ew-resize' }}
        >
          {renderTimeSignatures}
          {renderMeasureMarkers}
          {renderBeatMarkers}
          {renderChangeIndicators}
        </div>
        <div style={{ position: 'relative', height: 'calc(100% - 30px)' }} ref={gridRef}>
          {renderGridLines}
        </div>
      </div>
    </div>
  );
};

export default TimeGrid;
